import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import ScrollReveal from "../ScrollReveal/ScrollReveal";
import ProjectCard from "./ProjectCards";

import Smartattendance from "../../Assets/Projects/smartattendance.png";
import SayatriDriver from "../../Assets/Projects/sayatri_driver.png";
import Sayatri from "../../Assets/Projects/sayatri.png";
import Aikon from "../../Assets/aikon.svg";
import TwoKlips from "../../Assets/2klips.svg";
import a2z_online from "../../Assets/Projects/a2z_online.png";
import "./project.css";

const experience = [
  {
    company: "Hotstone Innovation",
    role: "Team Lead / Principal Engineer",
    period: "Feb 2025 - Present",
    stack: "Backend (Node/NestJS), Flutter, DevOps, Database Design",
    points: [
      "Led backend delivery using NestJS and PostgreSQL for production mobile and web ecosystems.",
      "Designed reusable modules and common patterns including DTO validation, guards, and error handling.",
      "Built and maintained Super Admin and CMS tooling for mobile apps and landing pages.",
      "Implemented real-time workflows using webhooks and events with retries, idempotency, and auditing.",
      "Owned end-to-end delivery across requirements, architecture, deployment, and production support.",
    ],
  },
  {
    company: "OAN Solutions",
    role: "Team Lead",
    period: "Sep 2024 - Jan 2025",
    stack: "Node.js/NestJS, PostgreSQL",
    points: [
      "Led a team delivering backend services and REST APIs using Node.js/NestJS with PostgreSQL.",
      "Improved API consistency and database structure for maintainability and production readiness.",
      "Collaborated with QA and stakeholders to ship stable releases and resolve production issues.",
    ],
  },
  {
    company: "Sayatri Technologies Pvt. Ltd.",
    role: "Team Lead",
    period: "Jan 2023 - Aug 2024",
    stack: "Node.js, MySQL, Flutter",
    points: [
      "Led development of ride-sharing platform features across backend services, admin tooling, and mobile apps.",
      "Designed relational schemas and REST APIs supporting core trip and operations workflows.",
      "Focused on performance, reliability, and scalable patterns across a growing product.",
    ],
  },
  {
    company: "Des Vu Technologies",
    role: "Full Stack Developer",
    period: "Oct 2021 - Jan 2023",
    stack: "Node.js, PostgreSQL, React, Flutter",
    points: [
      "Built backend services and REST APIs for web and mobile clients using Node.js with PostgreSQL.",
      "Worked on integration flows and end-to-end features with cross-functional teams.",
      "Maintained clean architecture and scalable patterns to meet client requirements.",
    ],
  },
];

const earlierRoles = [
  "Tuki Soft Pvt. Ltd. - Mobile Application Developer (May 2021 - Aug 2021): Flutter apps and API integration.",
  "Ad Break Media - Mobile Application Developer (Jan 2021 - May 2021): Offerwall and monetization SDK integrations for Android and iOS use cases.",
];

const projects = [
  {
    imgPath: TwoKlips,
    title: "2Klips",
    tag: "Dating App Ecosystem",
    description:
      "Dating app ecosystem spanning web, mobile, Super Admin, CMS, and insights tooling for activity visibility and platform operations.",
    links: [
      { label: "2klips.com", href: "https://2klips.com" },
      { label: "web.2klips.com/swipe", href: "https://web.2klips.com/swipe" },
      { label: "vendor.2klips.com/landing", href: "https://vendor.2klips.com/landing" },
      {
        label: "iOS App",
        href: "https://apps.apple.com/us/app/2klips/id6757687099",
      },
      {
        label: "Android App",
        href: "https://play.google.com/store/apps/details?id=com.twoklips.dating_app",
      },
    ],
    note: "Available on both iOS App Store and Google Play.",
  },
  {
    imgPath: Aikon,
    title: "Aikon",
    tag: "B2B Communication Platform",
    description:
      "B2B communication and selling platform for global traders delivered across product surfaces with backend and app support.",
    links: [
      { label: "aikon.app", href: "https://aikon.app" },
      {
        label: "Android App",
        href: "https://play.google.com/store/apps/details?id=app.aikon.business",
      },
      {
        label: "iOS App",
        href: "https://apps.apple.com/in/app/aikon/id6541762807",
      },
    ],
    note: "Available on Android and iOS.",
  },
  {
    title: "Rai Loyalty",
    tag: "Restaurant Loyalty Platform",
    description:
      "Loyalty app and web portal for RAI Restaurant, centered on member rewards, exclusive offers, notifications, and loyalty point redemption journeys.",
    links: [
      {
        label: "App Store",
        href: "https://apps.apple.com/us/app/rai-loyalty/id6758451257",
      },
      {
        label: "Loyalty Portal",
        href: "https://loyalty.rairestaurant.com/login",
      },
      {
        label: "RAI Restaurant",
        href: "https://rairestaurant.com/",
      },
    ],
    note: "Loyalty product for RAI Restaurant.",
  },
  {
    imgPath: Sayatri,
    title: "Sayatri / MeroAto",
    tag: "Ride-sharing Platform",
    description:
      "Ride-sharing platform spanning backend services, admin tooling, public web presence, and mobile apps for users and drivers.",
    links: [
      { label: "sayatri.com", href: "https://sayatri.com" },
      { label: "meroato.tukisoft.com.np", href: "https://meroato.tukisoft.com.np" },
    ],
    note: "Driver application available through Google Play.",
  },
  {
    imgPath: Smartattendance,
    title: "Offerwall SDKs",
    tag: "SDK Integrations",
    description:
      "Offerwall and monetization SDK integrations for Android and iOS use cases during earlier mobile engineering work.",
    links: [{ label: "adbreakmedia.com", href: "https://adbreakmedia.com" }],
  },
  {
    imgPath: a2z_online,
    title: "LMS System",
    tag: "Education Platform",
    description:
      "Learning management system supporting educational delivery and platform workflows.",
    links: [{ label: "a2zonlineinstitute.com", href: "https://a2zonlineinstitute.com" }],
  },
  {
    title: "MedprepNepal",
    tag: "Training Platform",
    description:
      "MBBS medical training and testing platform delivered as part of broader web product work.",
  },
  {
    imgPath: SayatriDriver,
    title: "Ridemandu",
    tag: "Mobility Product",
    description:
      "Ride-sharing related product work across backend services and product operations in the mobility domain.",
  },
];

function Projects() {
  return (
    <Container fluid className="project-section">
      <Container className="section-shell">
        <ScrollReveal as="section" className="experience-section" distance={24}>
          <div className="section-intro compact">
            <span className="section-kicker">Experience</span>
            <h2>Recent roles and delivery ownership.</h2>
          </div>
          <div className="experience-list">
            {experience.map((item) => (
              <ScrollReveal
                as="article"
                className="experience-card"
                key={`${item.company}-${item.period}`}
                delay={experience.indexOf(item) * 90}
                distance={20}
              >
                <div className="experience-head">
                  <div>
                    <h3>
                      {item.company} <span>{item.role}</span>
                    </h3>
                    <p>{item.stack}</p>
                  </div>
                  <strong>{item.period}</strong>
                </div>
                <ul className="experience-points">
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal className="experience-side-card earlier-roles" delay={110} distance={20}>
            <span className="section-kicker">Earlier Roles</span>
            {earlierRoles.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </ScrollReveal>
        </ScrollReveal>

        <ScrollReveal as="section" className="projects-hero" delay={90} distance={24}>
          <div className="section-intro center">
            <span className="section-kicker">Selected Work</span>
            <h1>Platforms and products I&apos;ve helped build in production.</h1>
            <p className="section-copy">
              Selected product work across backend systems, mobile delivery,
              admin tooling, integrations, and production-grade platform builds.
            </p>
          </div>
        </ScrollReveal>

        <Row className="projects-grid">
          {projects.map((project) => (
            <Col md={6} xl={4} className="project-card" key={project.title}>
              <ScrollReveal delay={projects.indexOf(project) * 85} distance={22}>
                <ProjectCard {...project} />
              </ScrollReveal>
            </Col>
          ))}
        </Row>
      </Container>
      <ScrollToTop />
    </Container>
  );
}

export default Projects;
